import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { environment } from '../../services/global-environment';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { GuidService } from '../../services/guid.service';
import { AuthService } from '../../services/auth.service';

export interface ApprovalPersonnel {
  id: string;
  username: string;
  fullName: string;
  position: string;
  email: string;
  department?: string;
}

interface ApiResponse {
  success: boolean;
  data: {
    id: string;
    username: string;
    fullName: string;
    position: string;
    department: string;
    email: string;
    enabled: boolean;
  }[];
  count: number;
}

interface ApprovalPersonnelMemory {
  selectedPersonnel: {
    key: string;
    value: string;
    email?: string;
    position?: string;
  }[];
  lastUpdated: string;
  version: string;
}

interface ApprovalLevel {
  id: string;                                      // 關卡唯一 ID
  level: number;                                   // 關卡編號 (1, 2, 3...)
  stepName: string;                                // 關卡名稱
  selectedPersonId: string;                        // 選擇的人員 ID
  selectedPersonData: any;                         // 完整人員資料
  searchTerm: string;                              // 每層獨立的搜尋關鍵字
  filteredPersonnel: ApprovalPersonnel[];          // 每層過濾後的人員清單
}

interface MultiLevelApprovalMemory {
  levels: {
    level: number;
    stepName: string;
    selectedPerson: {
      key: string;
      value: string;
      email?: string;
      position?: string;
      department?: string;
    } | null;
  }[];
  lastUpdated: string;
  version: string;
}

@Component({
  selector: 'app-form-approval-personnel-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, TranslatePipe],
  template: `
    <div class="form-section approval-personnel-section">
      <!-- 標籤（只在單層模式顯示） -->
      <label *ngIf="!multiLevel" [attr.for]="id" [class.required-field]="required">
        <i class="fas fa-user-check"></i> {{ label }}
      </label>

      <!-- 載入中指示器 -->
      <div *ngIf="isLoading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <span>{{ 'form.approval_personnel.loading' | translate }}</span>
      </div>

      <!-- 錯誤訊息 -->
      <div *ngIf="errorMessage" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <!-- 單層模式 UI -->
      <ng-container *ngIf="!multiLevel">
        <!-- Search box -->
        <div *ngIf="!isLoading && !hideSearch" class="search-container">
          <input
            type="text"
            class="search-input"
            [(ngModel)]="searchTerm"
            (ngModelChange)="onSearchChange()"
            [placeholder]="'form.approval_personnel.search_placeholder' | translate"
            [disabled]="disabled"
          >
        </div>

        <!-- Select dropdown -->
        <div *ngIf="!isLoading" class="select-container">
          <select
            [id]="id"
            [name]="name"
            [(ngModel)]="value"
            (ngModelChange)="onSelectionChange($event)"
            [disabled]="disabled"
            class="form-select personnel-select"
          >
            <option value="">{{ placeholder }}</option>
            <option
              *ngFor="let person of filteredPersonnel; trackBy: trackByPersonId"
              [value]="person.id"
            >
              {{ person.fullName }} - {{ person.department || '' }} {{ person.position }}({{ person.email }})
            </option>
          </select>
        </div>

        <!-- Message when search results are 0 -->
        <div *ngIf="!isLoading && searchTerm && filteredPersonnel.length === 0" class="no-results">
          <i class="fas fa-search"></i>
          <span>{{ 'form.approval_personnel.no_search_results' | translate }}</span>
        </div>
      </ng-container>

      <!-- 多層級模式 UI -->
      <ng-container *ngIf="multiLevel && !isLoading">
        <div class="multi-level-container">
          <!-- 標題列：左側標題 + 右側新增按鈕 -->
          <div class="header-row">
            <div class="header-title">
              <i class="fas fa-user-check"></i>
              <span>{{ label }}</span>
            </div>

            <!-- 新增關卡按鈕（右上角，只在有選擇人員時顯示） -->
            <button
              *ngIf="currentSelectedPersonId && currentSelectedPersonData"
              type="button"
              class="btn-add-top-right"
              (click)="addLevelFromCurrent()"
              [disabled]="disabled || levels.length >= maxLevels"
              [title]="'form.approval_personnel.add_level' | translate"
            >
              <i class="fas fa-plus-circle"></i>
              <span class="btn-text">{{ 'form.approval_personnel.add_level' | translate }}</span>
            </button>
          </div>

          <!-- 目前選擇區（搜尋框+下拉選單） -->
          <div class="current-selector">
            <div class="selector-row">
              <!-- 搜尋框 -->
              <input
                type="text"
                class="search-input"
                [(ngModel)]="currentSearchTerm"
                (ngModelChange)="onCurrentSearchChange()"
                [placeholder]="'form.approval_personnel.search_placeholder' | translate"
                [disabled]="disabled"
              >

              <!-- 人員選擇下拉選單 -->
              <select
                class="form-select personnel-select"
                [(ngModel)]="currentSelectedPersonId"
                (ngModelChange)="onCurrentSelectionChange($event)"
                [disabled]="disabled"
              >
                <option value="">{{ 'form.approval_personnel.select_approver' | translate }}</option>
                <option
                  *ngFor="let person of currentFilteredPersonnel; trackBy: trackByPersonId"
                  [value]="person.id"
                >
                  {{ person.fullName }} - {{ person.department || '' }} {{ person.position }}
                </option>
              </select>
            </div>

            <!-- 目前已選擇人員提示 -->
            <div *ngIf="currentSelectedPersonData && levels.length === 0" class="current-selection-hint">
              <i class="fas fa-check-circle text-success"></i>
              <span>{{ 'form.approval_personnel.current_selection' | translate }}: {{ currentSelectedPersonData.fullName }}</span>
            </div>
          </div>

          <!-- 已選擇的關卡摘要（只在有選擇時顯示，放在加號下方） -->
          <div class="selected-levels-summary" *ngIf="getCompletedLevels().length > 0">
            <div *ngFor="let level of getCompletedLevels()" class="level-summary-item">
              <button
                type="button"
                class="btn-remove-mini"
                (click)="removeLevel(level.id)"
                [disabled]="disabled"
                [title]="'form.approval_personnel.remove_level' | translate"
              >
                <i class="fas fa-times"></i>
              </button>
              <span class="step-badge">{{ level.stepName }}</span>
              <span class="person-name">{{ level.selectedPersonData?.fullName }}</span>
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormApprovalPersonnelSelectorComponent),
      multi: true
    }
  ],
  styleUrls: ['./form-approval-personnel-selector.component.scss']
})
export class FormApprovalPersonnelSelectorComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() multiSelect: boolean = false;
  @Input() disabled: boolean = false;
  @Input() hideSearch: boolean = false;
  
  // 記憶功能相關
  @Input() enableMemory: boolean = true;
  @Input() memoryKey: string = ''; // 直接使用 UUID 作為記憶鍵
  
  // 權限控制相關（為未來擴展預留）
  @Input() allowedRoles: string[] = [];
  @Input() allowedDepartments: string[] = [];
  @Input() restrictToCurrentDepartment: boolean = false;

  // 多層級模式相關（新增）
  @Input() multiLevel: boolean = true;            // 是否啟用多層級模式（預設啟用）
  @Input() maxLevels: number = 5;                 // 最大關卡數
  @Input() minLevels: number = 1;                 // 最小關卡數
  @Input() defaultLevels: number = 1;             // 預設關卡數（預設 1 層）

  value: string | string[] = '';
  searchTerm: string = '';
  personnel: ApprovalPersonnel[] = [];
  filteredPersonnel: ApprovalPersonnel[] = [];
  selectedPersonnelDisplay: ApprovalPersonnel[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';
  
  // 記憶功能狀態（靜默運作，不顯示 UI）

  // 選擇的人員狀態
  selectedPersonnel: ApprovalPersonnel | null = null;

  // 多層級模式專用屬性
  levels: ApprovalLevel[] = [];

  // 目前選擇區（簡潔模式專用）
  currentSearchTerm: string = '';
  currentSelectedPersonId: string = '';
  currentSelectedPersonData: any = null;
  currentFilteredPersonnel: ApprovalPersonnel[] = [];

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private http: HttpClient, 
    private i18nService: I18nService,
    private guidService: GuidService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    if (this.multiLevel) {
      this.initializeMultiLevel();
    } else {
      this.initializeComponent();
    }
  }

  private async initializeComponent(): Promise<void> {
    // 設定預設值
    if (!this.label) {
      this.label = this.i18nService.translate('form.approval_personnel.title');
    }
    if (!this.placeholder) {
      this.placeholder = this.i18nService.translate('form.approval_personnel.placeholder');
    }

    // 先載入人員資料，再應用記憶資料
    await this.loadPersonnelData();

    // 人員資料載入完成後，載入並應用記憶資料
    if (this.enableMemory) {
      this.loadMemoryData();
      
      // Final check after initialization to verify memory data is correctly applied
      setTimeout(() => {
        if (this.value && this.personnel.length > 0) {
          const selectedPerson = this.personnel.find(person => person.id === this.value);
          if (selectedPerson) {
            // Execute only if onChange has not been executed yet
            const approvalFlowData = {
              type: "approval-personnel-selector",
              value: {
                flowId: this.guidService.generateGuid(),
                flowName: "specifial user",
                flowDescription: "specifial user",
                steps: [
                  {
                    stepDefinitionId: this.guidService.generateGuid(),
                    level: 1,
                    stepName: "Step1",
                    approverType: "specific_user",
                    requireComment: true,
                    requireSignature: true,
                    optional: false,
                    userId: selectedPerson.id,
                    userName: selectedPerson.username,
                    isDynamicDepartment: false,
                    approverList: [
                      {
                        id: selectedPerson.id,
                        name: selectedPerson.username
                      }
                    ]
                  }
                ]
              }
            };
            this.onChange(approvalFlowData);
          }
        }
      }, 300);
    }
  }

  /**
   * 多層級模式初始化（簡潔版）
   */
  private async initializeMultiLevel(): Promise<void> {
    // 設定預設值
    if (!this.label) {
      this.label = this.i18nService.translate('form.approval_personnel.multi_level_title');
    }

    // 先載入人員資料
    await this.loadPersonnelData();

    // 初始化目前選擇區
    this.currentSearchTerm = '';
    this.currentSelectedPersonId = '';
    this.currentSelectedPersonData = null;
    this.currentFilteredPersonnel = [...this.personnel];

    // 清空已選關卡
    this.levels = [];

    // 載入記憶資料
    if (this.enableMemory) {
      this.loadMultiLevelMemoryData();
    }
  }

  /**
   * 初始化預設關卡數（已不使用，改為按需新增）
   */
  private initializeDefaultLevels(): void {
    // 不再預設建立任何關卡，讓使用者按需新增
    this.levels = [];
  }

  /**
   * 載入多層級記憶資料
   * @returns true 如果成功載入記憶資料，false 如果沒有記憶資料
   */
  private loadMultiLevelMemoryData(): boolean {
    try {
      console.log('開始載入多層級記憶資料，storageKey:', this.storageKey);
      const memoryData = localStorage.getItem(this.storageKey);
      if (!memoryData) {
        console.log('沒有找到多層級記憶資料');
        return false;
      }

      const parsed: any = JSON.parse(memoryData);
      console.log('找到記憶資料:', parsed);

      // 🔄 支援舊版格式（version 1.0.0）轉換為新版格式（version 2.0.0）
      if (parsed.version === '1.0.0' && parsed.selectedPersonnel && Array.isArray(parsed.selectedPersonnel)) {
        console.log('⚠️ 偵測到舊版記憶資料格式（v1.0.0），轉換為多層級格式...');

        // 將舊版單層/多選格式轉換為多層級格式
        this.levels = [];
        parsed.selectedPersonnel.forEach((person: any, index: number) => {
          const personData = this.personnel.find(p => p.id === person.key);

          if (personData) {
            const level: ApprovalLevel = {
              id: this.guidService.generateGuid(),
              level: index + 1,
              stepName: `Step${index + 1}`,
              selectedPersonId: person.key,
              selectedPersonData: personData,
              searchTerm: '',
              filteredPersonnel: [...this.personnel]
            };
            this.levels.push(level);
          } else {
            console.log(`舊版記憶資料中的人員不存在: ${person.value} (${person.key})`);
          }
        });

        console.log('✅ 成功轉換舊版記憶資料為多層級格式，levels:', this.levels);

        // ⭐ 重要：轉換後必須通知表單更新
        this.emitMultiLevelValue();

        // 轉換後自動儲存為新版格式
        this.saveMultiLevelMemoryData();
        return true;
      }

      // 檢查新版資料格式（version 2.0.0）
      if (parsed.version === '2.0.0' && parsed.levels && Array.isArray(parsed.levels)) {
        console.log('✅ 多層級記憶資料結構正確，levels:', parsed.levels);

        // 重建 levels 陣列
        this.levels = [];
        for (const levelMemory of parsed.levels) {
          // 從記憶資料中提取人員 ID
          const rememberedPersonId = levelMemory.selectedPerson ? levelMemory.selectedPerson.key : '';

          // 🔍 從當前人員列表中找到完整的人員資料
          const fullPersonData = rememberedPersonId
            ? this.personnel.find(p => p.id === rememberedPersonId)
            : null;

          const level: ApprovalLevel = {
            id: this.guidService.generateGuid(),
            level: levelMemory.level,
            stepName: levelMemory.stepName,
            selectedPersonId: fullPersonData ? fullPersonData.id : '',
            selectedPersonData: fullPersonData || null,  // 使用完整的人員資料
            searchTerm: '',
            filteredPersonnel: [...this.personnel]
          };

          // 驗證記憶的人員是否仍存在於當前人員列表中
          if (rememberedPersonId && !fullPersonData) {
            console.log(`關卡 ${level.level} 記憶的人員不存在 (ID: ${rememberedPersonId})，清除該選擇`);
          } else if (fullPersonData) {
            console.log(`✅ 關卡 ${level.level} 成功載入人員: ${fullPersonData.fullName} (${fullPersonData.id})`);
          }

          this.levels.push(level);
        }

        console.log('✅ 載入多層級記憶資料成功，levels:', this.levels);

        // ⭐ 重要：載入記憶資料後，必須通知表單更新
        this.emitMultiLevelValue();

        return true;
      }

      console.log('❌ 記憶資料版本或格式不正確');
      return false;
    } catch (error) {
      console.warn('❌ 載入多層級記憶資料失敗:', error);
      return false;
    }
  }

  /**
   * 儲存多層級記憶資料（公開方法，供外部表單儲存時呼叫）
   */
  saveMultiLevelMemoryData(): void {
    if (!this.enableMemory || !this.multiLevel) return;

    try {
      const memoryData: MultiLevelApprovalMemory = {
        levels: this.levels.map(level => ({
          level: level.level,
          stepName: level.stepName,
          selectedPerson: level.selectedPersonData ? {
            key: level.selectedPersonData.id,
            value: level.selectedPersonData.fullName,
            email: level.selectedPersonData.email,
            position: level.selectedPersonData.position,
            department: level.selectedPersonData.department
          } : null
        })),
        lastUpdated: new Date().toISOString(),
        version: '2.0.0'
      };

      localStorage.setItem(this.storageKey, JSON.stringify(memoryData));
      console.log('儲存多層級記憶資料:', memoryData);
    } catch (error) {
      console.warn('儲存多層級記憶資料失敗:', error);
    }
  }

  /**
   * 新增關卡
   */
  addLevel(): void {
    if (!this.multiLevel) return;

    // 檢查是否已達到最大關卡數
    if (this.levels.length >= this.maxLevels) {
      console.warn(`已達到最大關卡數：${this.maxLevels}`);
      return;
    }

    const newLevel: ApprovalLevel = {
      id: this.guidService.generateGuid(),
      level: this.levels.length + 1,
      stepName: `Step${this.levels.length + 1}`,
      selectedPersonId: '',
      selectedPersonData: null,
      searchTerm: '',
      filteredPersonnel: [...this.personnel]
    };

    this.levels.push(newLevel);
    console.log('新增關卡:', newLevel);

    // 通知表單變更
    this.emitMultiLevelValue();
  }

  /**
   * 刪除關卡
   */
  removeLevel(levelId: string): void {
    if (!this.multiLevel) return;

    // 找到要刪除的關卡索引
    const index = this.levels.findIndex(l => l.id === levelId);
    if (index === -1) {
      console.warn('找不到要刪除的關卡:', levelId);
      return;
    }

    // 刪除關卡
    this.levels.splice(index, 1);
    console.log('刪除關卡:', levelId, '剩餘關卡數:', this.levels.length);

    // 重新編號剩餘關卡
    if (this.levels.length > 0) {
      this.renumberLevels();
    }

    // 通知表單變更
    this.emitMultiLevelValue();

    // ✅ 刪除關卡後立即更新記憶資料
    if (this.enableMemory) {
      this.saveMultiLevelMemoryData();
    }
  }

  /**
   * 重新編號所有關卡
   */
  private renumberLevels(): void {
    this.levels.forEach((level, index) => {
      level.level = index + 1;
      // 如果 stepName 是預設格式（StepN），則更新編號
      if (level.stepName.match(/^Step\d+$/)) {
        level.stepName = `Step${index + 1}`;
      }
    });
    console.log('重新編號關卡完成:', this.levels);
  }

  /**
   * 多層級模式的人員選擇變更處理
   */
  onMultiLevelSelectionChange(levelId: string, personId: string): void {
    const level = this.levels.find(l => l.id === levelId);
    if (!level) return;

    level.selectedPersonId = personId;

    if (personId) {
      const selectedPerson = this.personnel.find(p => p.id === personId);
      if (selectedPerson) {
        level.selectedPersonData = selectedPerson;
        console.log(`關卡 ${level.level} 選擇人員:`, selectedPerson);
      }
    } else {
      level.selectedPersonData = null;
      console.log(`關卡 ${level.level} 清除選擇`);
    }

    // 通知表單變更
    this.emitMultiLevelValue();

    // ✅ 選擇變更後立即儲存記憶資料
    if (this.enableMemory) {
      this.saveMultiLevelMemoryData();
    }
  }

  /**
   * 發送多層級簽核流程資料
   */
  private emitMultiLevelValue(): void {
    // 建立符合資料庫格式的簽核流程資料
    const steps = this.levels
      .filter(level => level.selectedPersonId) // 只包含有選擇人員的關卡
      .map(level => ({
        stepDefinitionId: this.guidService.generateGuid(),
        level: level.level,
        stepName: level.stepName,
        requireComment: true,
        requireSignature: true,
        optional: false,
        // ⭐ 新增 approvers 陣列結構（與 FormApprovalFlowComponent 相同）
        approvers: [
          {
            type: 'specific_user',
            userId: level.selectedPersonId,
            userName: level.selectedPersonData?.fullName || ''
          }
        ],
        // ⭐ 新增 approvalLogic 欄位
        approvalLogic: 'any_one',
        // ⭐ approverList 包含所有可簽核的人員
        approverList: [
          {
            id: level.selectedPersonId,
            name: level.selectedPersonData?.fullName || ''
          }
        ]
      }));

    if (steps.length === 0) {
      // 沒有選擇任何人員
      this.onChange(null);
      return;
    }

    const approvalFlowData = {
      type: 'approval-personnel-selector',  // 保持原有的元件類型識別
      value: {
        flowId: this.guidService.generateGuid(),
        flowName: 'Multi-level Approval',
        flowDescription: `${steps.length} level approval flow`,
        steps: steps
      }
    };

    console.log('🔔 === 發送多層級簽核流程資料 ===');
    console.log('完整資料:', JSON.stringify(approvalFlowData, null, 2));
    console.log('第一步驟 approverList:', steps[0]?.approverList);
    console.log('第一步驟 approvers:', steps[0]?.approvers);
    this.onChange(approvalFlowData);
  }

  /**
   * 取得已完成選擇的關卡列表（用於顯示摘要）
   */
  getCompletedLevels(): ApprovalLevel[] {
    return this.levels.filter(level => level.selectedPersonId && level.selectedPersonData);
  }

  /**
   * 目前選擇區：搜尋處理
   */
  onCurrentSearchChange(): void {
    if (!this.currentSearchTerm || this.currentSearchTerm.trim() === '') {
      this.currentFilteredPersonnel = [...this.personnel];
      return;
    }

    const searchLower = this.currentSearchTerm.toLowerCase().trim();
    this.currentFilteredPersonnel = this.personnel.filter(person => {
      const nameMatch = person.fullName.toLowerCase().includes(searchLower);
      const positionMatch = person.position?.toLowerCase().includes(searchLower) || false;
      const departmentMatch = person.department?.toLowerCase().includes(searchLower) || false;

      return nameMatch || positionMatch || departmentMatch;
    });
  }

  /**
   * 目前選擇區：人員選擇處理
   */
  onCurrentSelectionChange(personId: string): void {
    this.currentSelectedPersonId = personId;

    if (personId) {
      const selectedPerson = this.personnel.find(p => p.id === personId);
      if (selectedPerson) {
        this.currentSelectedPersonData = selectedPerson;
        console.log('目前選擇人員:', selectedPerson);
      }
    } else {
      this.currentSelectedPersonData = null;
    }

    // 如果沒有其他關卡，直接發送單層資料
    if (this.levels.length === 0 && personId) {
      this.emitSingleLevelValue();
    }
  }

  /**
   * 發送單層簽核資料（只有一個人員）
   */
  private emitSingleLevelValue(): void {
    if (!this.currentSelectedPersonData) {
      this.onChange(null);
      return;
    }

    const approvalFlowData = {
      type: 'approval-personnel-selector',  // 保持原有的元件類型識別
      value: {
        flowId: this.guidService.generateGuid(),
        flowName: 'Single Level Approval',
        flowDescription: '單層簽核流程',
        steps: [
          {
            stepDefinitionId: this.guidService.generateGuid(),
            level: 1,
            stepName: 'Step1',
            requireComment: true,
            requireSignature: true,
            optional: false,
            // ⭐ approvers 陣列結構（與 FormApprovalFlowComponent 相同）
            approvers: [
              {
                type: 'specific_user',
                userId: this.currentSelectedPersonData.id,
                userName: this.currentSelectedPersonData.fullName
              }
            ],
            // ⭐ 簽核邏輯
            approvalLogic: 'any_one',
            // ⭐ approverList 包含所有可簽核的人員
            approverList: [
              {
                id: this.currentSelectedPersonData.id,
                name: this.currentSelectedPersonData.fullName
              }
            ]
          }
        ]
      }
    };

    console.log('🔔 === 發送單層簽核流程資料 ===');
    console.log('完整資料:', JSON.stringify(approvalFlowData, null, 2));
    console.log('第一步驟 approverList:', approvalFlowData.value.steps[0].approverList);
    console.log('第一步驟 approvers:', approvalFlowData.value.steps[0].approvers);
    this.onChange(approvalFlowData);
  }

  /**
   * 從目前選擇區新增關卡
   */
  addLevelFromCurrent(): void {
    console.log('=== addLevelFromCurrent 被呼叫 ===');
    console.log('currentSelectedPersonId:', this.currentSelectedPersonId);
    console.log('currentSelectedPersonData:', this.currentSelectedPersonData);
    console.log('levels.length:', this.levels.length);
    console.log('maxLevels:', this.maxLevels);

    if (!this.currentSelectedPersonId || !this.currentSelectedPersonData) {
      console.warn('❌ 請先選擇人員');
      alert('請先在下拉選單中選擇一位人員');
      return;
    }

    if (this.levels.length >= this.maxLevels) {
      console.warn(`❌ 已達到最大關卡數：${this.maxLevels}`);
      alert(`已達到最大關卡數：${this.maxLevels}`);
      return;
    }

    // 建立新關卡
    const newLevel: ApprovalLevel = {
      id: this.guidService.generateGuid(),
      level: this.levels.length + 1,
      stepName: `Step${this.levels.length + 1}`,
      selectedPersonId: this.currentSelectedPersonId,
      selectedPersonData: this.currentSelectedPersonData,
      searchTerm: '',
      filteredPersonnel: [...this.personnel]
    };

    this.levels.push(newLevel);
    console.log('✅ 新增關卡成功:', newLevel);

    // 清空目前選擇區，準備選下一個
    this.currentSearchTerm = '';
    this.currentSelectedPersonId = '';
    this.currentSelectedPersonData = null;
    this.currentFilteredPersonnel = [...this.personnel];

    // 發送更新後的多層級資料
    this.emitMultiLevelValue();

    // ✅ 新增關卡後立即儲存記憶資料
    if (this.enableMemory) {
      this.saveMultiLevelMemoryData();
    }
  }

  private get storageKey(): string {
    // 如果沒有提供 memoryKey，使用一個固定的預設值
    // 這樣每個表單的記憶功能會共享同一個 key
    if (!this.memoryKey) {
      this.memoryKey = 'default-approval';
    }
    
    const companyid = environment.companyid;
    return `approval-personnel-${companyid}-${this.memoryKey}`;
  }

  private loadMemoryData(): void {
    try {
      console.log('開始載入記憶資料，storageKey:', this.storageKey);
      const memoryData = localStorage.getItem(this.storageKey);
      if (memoryData) {
        const parsed: ApprovalPersonnelMemory = JSON.parse(memoryData);
        console.log('找到記憶資料:', parsed);
        
        // 檢查資料版本和完整性
        if (parsed.version === '1.0.0' && parsed.selectedPersonnel && Array.isArray(parsed.selectedPersonnel)) {
          console.log('記憶資料結構正確，selectedPersonnel:', parsed.selectedPersonnel);
          // 設定記憶資料到表單值
          if (this.multiSelect) {
            this.value = parsed.selectedPersonnel.map(p => p.key);
          } else if (parsed.selectedPersonnel.length > 0) {
            const rememberedPersonId = parsed.selectedPersonnel[0].key;
            
            // 確保人員在當前列表中存在
            const personExists = this.personnel.find(person => person.id === rememberedPersonId);
            if (personExists) {
              this.value = rememberedPersonId;
              console.log('設定記憶值 (人員ID):', this.value);
              console.log('記憶的人員資訊:', parsed.selectedPersonnel[0]);
              
              // 立即更新顯示狀態
              this.updateSelectedPersonnelDisplay();
              
              // Ensure form control knows this value has been set and trigger onChange event
              setTimeout(() => {
                if (this.value === rememberedPersonId && this.personnel.length > 0) {
                  console.log('確認記憶值已正確設定到表單控制項:', this.value);
                  // Execute onChange for approver restored from memory data
                  const selectedPerson = this.personnel.find(person => person.id === rememberedPersonId);
                  if (selectedPerson) {
                    this.saveMemoryData(); // Update memory data as well
                    const approvalFlowData = {
                      type: "approval-personnel-selector",
                      value: {
                        flowId: this.guidService.generateGuid(),
                        flowName: "specifial user",
                        flowDescription: "specifial user",
                        steps: [
                          {
                            stepDefinitionId: this.guidService.generateGuid(),
                            level: 1,
                            stepName: "Step1",
                            approverType: "specific_user",
                            requireComment: true,
                            requireSignature: true,
                            optional: false,
                            userId: selectedPerson.id,
                            userName: selectedPerson.username,
                            isDynamicDepartment: false,
                            approverList: [
                              {
                                id: selectedPerson.id,
                                name: selectedPerson.username
                              }
                            ]
                          }
                        ]
                      }
                    };
                    this.onChange(approvalFlowData);
                  }
                }
              }, 200);
            } else {
              console.log('記憶的人員在當前列表中不存在，清除記憶');
              this.clearMemory();
            }
          }
          
          console.log('載入記憶資料成功，current value:', this.value);
        } else {
          console.log('記憶資料版本或格式不正確');
        }
      } else {
        console.log('沒有找到記憶資料');
      }
    } catch (error) {
      console.warn('載入記憶資料失敗:', error);
      // 清除損壞的記憶資料
      this.clearMemory();
    }
  }

  private saveMemoryData(): void {
    if (!this.enableMemory) return;

    try {
      const selectedPersonnel = this.getSelectedPersonnelForMemory();
      
      if (selectedPersonnel.length === 0) {
        // 如果沒有選擇，清除記憶
        this.clearMemory();
        return;
      }

      const memoryData: ApprovalPersonnelMemory = {
        selectedPersonnel: selectedPersonnel,
        lastUpdated: new Date().toISOString(),
        version: '1.0.0'
      };

      localStorage.setItem(this.storageKey, JSON.stringify(memoryData));
      console.log('儲存記憶資料:', memoryData);
      console.log('儲存到 localStorage key:', this.storageKey);
      console.log('當前選擇的值 (this.value):', this.value);
    } catch (error) {
      console.warn('儲存記憶資料失敗:', error);
    }
  }

  private getSelectedPersonnelForMemory(): { key: string; value: string; email?: string; position?: string; }[] {
    if (!this.selectedPersonnelDisplay || this.selectedPersonnelDisplay.length === 0) {
      return [];
    }

    return this.selectedPersonnelDisplay.map(person => ({
      key: person.id,
      value: person.fullName,
      email: person.email,
      position: person.position
    }));
  }


  clearMemory(): void {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('清除記憶資料');
    } catch (error) {
      console.warn('清除記憶資料失敗:', error);
    }
  }

  private async loadPersonnelData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const companyid = environment.companyid;

      if (!companyid) {
        console.warn('FormApprovalPersonnelSelector: companyid not available from environment');
        this.handleNoDataResponse();
        return;
      }

      const apiUrl = environment.apiUrl || '';
      const url = `${apiUrl}/api/users/approval-personnel`;

      console.log(`FormApprovalPersonnelSelector: Fetching personnel data from ${url}`);

      const response = await this.http.get<ApiResponse>(url).pipe(
        catchError(error => {
          console.error('載入簽核人員資料失敗:', error);
          this.errorMessage = this.i18nService.translate('form.approval_personnel.load_error_fallback');
          return of(null);
        })
      ).toPromise();

      if (response?.success && response.data && Array.isArray(response.data)) {
        // 轉換 API 資料格式
        this.personnel = response.data.map(user => ({
          id: user.id,
          username: user.username,
          fullName: user.fullName,
          position: user.position,
          email: user.email,
          department: user.department // Add department information
        }));

        // Filter out current user from approval personnel list
        const currentUser = this.authService.getUserInfo();
        if (currentUser.userid) {
          const originalCount = this.personnel.length;
          this.personnel = this.personnel.filter(person => person.id !== currentUser.userid);
          console.log(`FormApprovalPersonnelSelector: Filtered out current user (${currentUser.userid}) from approval personnel list. Personnel count: ${originalCount} -> ${this.personnel.length}`);
        }

        // 應用權限篩選（為未來擴展預留）
        this.applyPermissionFilters();

        console.log(`FormApprovalPersonnelSelector: Loaded ${this.personnel.length} personnel records`);
        this.filteredPersonnel = [...this.personnel];
        this.updateSelectedPersonnelDisplay();
        this.filterPersonnel(); // Initial filtering
        
        // 人員資料載入完成後，重新檢查記憶資料是否需要應用
        if (this.value && this.enableMemory) {
          const selectedPerson = this.personnel.find(person => person.id === this.value);
          if (selectedPerson) {
            console.log('應用記憶資料，選擇人員:', selectedPerson);
            // 確保 UI 狀態與記憶資料同步
            this.updateSelectedPersonnelDisplay();
            // 觸發 onChange 事件讓父組件知道值已經載入 - 使用簽核流程格式
            const approvalFlowData = {
              type: "approval-personnel-selector",
              value: {
                flowId: this.guidService.generateGuid(), // 生成虛擬流程ID
                flowName: "specifial user",
                flowDescription: "specifial user",
                steps: [
                  {
                    stepDefinitionId: this.guidService.generateGuid(),
                    level: 1,
                    stepName: "Step1",
                    approverType: "specific_user",
                    requireComment: true,
                    requireSignature: true,
                    optional: false,
                    userId: selectedPerson.id,
                    userName: selectedPerson.username,
                    isDynamicDepartment: false,
                    approverList: [
                      {
                        id: selectedPerson.id,
                        name: selectedPerson.username
                      }
                    ]
                  }
                ]
              }
            };
            this.onChange(approvalFlowData);
          } else {
            console.log('記憶的人員ID在當前人員列表中找不到，清除記憶');
            this.clearMemory();
          }
        }
      } else {
        console.warn('FormApprovalPersonnelSelector: API response not as expected:', response);
        this.handleNoDataResponse();
      }
    } catch (error) {
      console.error('FormApprovalPersonnelSelector: Error loading personnel data:', error);
      this.errorMessage = this.i18nService.translate('form.approval_personnel.load_error');
      this.handleNoDataResponse();
    } finally {
      this.isLoading = false;
    }
  }

  private applyPermissionFilters(): void {
    // 為未來的權限控制功能預留
    // 可根據 allowedRoles, allowedDepartments, restrictToCurrentDepartment 等進行篩選
    
    // 範例實作（目前不做篩選）:
    // if (this.allowedRoles.length > 0) {
    //   this.personnel = this.personnel.filter(person => 
    //     this.allowedRoles.includes(person.roleId)
    //   );
    // }
  }

  private handleNoDataResponse(): void {
    this.personnel = [];
    this.filteredPersonnel = [];
  }

  onSelectionChange(personId: string): void {
    this.value = personId;
    this.updateSelectedPersonnelDisplay();
    
    if (personId) {
      // 有選擇人員時，儲存記憶
      this.saveMemoryData();
      const selectedPerson = this.personnel.find(person => person.id === personId);
      if (selectedPerson) {
        // 轉換為簽核流程格式 - 完全符合 approval-flow 的架構
        const approvalFlowData = {
          type: "approval-personnel-selector",
          value: {
            flowId: this.guidService.generateGuid(), // 生成虛擬流程ID
            flowName: "specifial user",
            flowDescription: "specifial user",
            steps: [
              {
                stepDefinitionId: this.guidService.generateGuid(),
                level: 1,
                stepName: "Step1",
                approverType: "specific_user",
                requireComment: true,
                requireSignature: true,
                optional: false,
                userId: selectedPerson.id,
                userName: selectedPerson.username,
                isDynamicDepartment: false,
                approverList: [
                  {
                    id: selectedPerson.id,
                    name: selectedPerson.username
                  }
                ]
              }
            ]
          }
        };
        this.onChange(approvalFlowData);
      }
    } else {
      // 選擇空值時，清除記憶
      this.clearMemory();
      this.onChange('');
    }
    
    this.onTouched();
  }

  onSingleSelectionChange(personId: string): void {
    this.value = personId;
    this.updateSelectedPersonnelDisplay();
    this.saveMemoryData();
    
    const selectedPerson = this.personnel.find(person => person.id === personId);
    const personnelData = {
      key: personId,
      value: selectedPerson ? selectedPerson.fullName : personId,
      email: selectedPerson?.email,
      position: selectedPerson?.position
    };
    
    this.onChange(personnelData);
  }

  onMultiSelectionChange(personId: string, event: any): void {
    const isChecked = event.target.checked;
    let selectedIds = Array.isArray(this.value) ? [...this.value] : [];

    if (isChecked) {
      if (!selectedIds.includes(personId)) {
        selectedIds.push(personId);
      }
    } else {
      selectedIds = selectedIds.filter(id => id !== personId);
    }

    this.value = selectedIds;
    this.updateSelectedPersonnelDisplay();
    this.saveMemoryData();
    
    const personnelDataArray = selectedIds.map(id => {
      const person = this.personnel.find(p => p.id === id);
      return {
        key: id,
        value: person ? person.fullName : id,
        email: person?.email,
        position: person?.position
      };
    });
    
    this.onChange(personnelDataArray);
  }

  isPersonSelected(personId: string): boolean {
    if (this.multiSelect) {
      return Array.isArray(this.value) && this.value.includes(personId);
    }
    return this.value === personId;
  }

  removePerson(personId: string): void {
    if (this.disabled) return;

    if (this.multiSelect) {
      const selectedIds = Array.isArray(this.value) ? this.value.filter(id => id !== personId) : [];
      this.value = selectedIds;
      this.onMultiSelectionChange(personId, { target: { checked: false } });
    } else {
      this.value = '';
      this.onSingleSelectionChange('');
    }
  }

  private updateSelectedPersonnelDisplay(): void {
    if (this.value) {
      // 單選模式 - Select 使用
      const selectedPerson = this.personnel.find(person => person.id === this.value);
      if (selectedPerson) {
        this.selectedPersonnel = selectedPerson;
        this.selectedPersonnelDisplay = [selectedPerson];
      } else {
        this.selectedPersonnel = null;
        this.selectedPersonnelDisplay = [];
      }
    } else {
      // 無選擇
      this.selectedPersonnel = null;
      this.selectedPersonnelDisplay = [];
    }
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    if (value !== undefined) {
      if (typeof value === 'string') {
        this.value = value;
      } else if (Array.isArray(value)) {
        // 處理陣列格式（多選模式或記憶資料）
        if (value.length > 0 && typeof value[0] === 'object' && value[0].key) {
          // 從記憶資料物件陣列中提取 key
          this.value = this.multiSelect ? value.map(item => item.key) : value[0].key;
        } else {
          // 直接的 ID 陣列
          this.value = this.multiSelect ? value : (value.length > 0 ? value[0] : '');
        }
      } else if (typeof value === 'object' && value.key) {
        // 單個物件格式
        this.value = value.key;
      } else {
        this.value = value;
      }
      
      this.updateSelectedPersonnelDisplay();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  trackByPersonId(index: number, person: ApprovalPersonnel): string {
    return person.id;
  }

  // Search functionality methods
  onSearchChange(): void {
    this.filterPersonnel();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filterPersonnel();
  }

  private filterPersonnel(): void {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredPersonnel = [...this.personnel];
      return;
    }

    const searchLower = this.searchTerm.toLowerCase().trim();
    this.filteredPersonnel = this.personnel.filter(person => {
      // Search by name, department, and position
      const nameMatch = person.fullName.toLowerCase().includes(searchLower);
      const positionMatch = person.position?.toLowerCase().includes(searchLower) || false;
      const departmentMatch = person.department?.toLowerCase().includes(searchLower) || false;

      return nameMatch || positionMatch || departmentMatch;
    });
  }

  /**
   * 多層級模式：每層獨立的搜尋功能
   */
  onLevelSearchChange(level: ApprovalLevel): void {
    if (!level.searchTerm || level.searchTerm.trim() === '') {
      level.filteredPersonnel = [...this.personnel];
      return;
    }

    const searchLower = level.searchTerm.toLowerCase().trim();
    level.filteredPersonnel = this.personnel.filter(person => {
      const nameMatch = person.fullName.toLowerCase().includes(searchLower);
      const positionMatch = person.position?.toLowerCase().includes(searchLower) || false;
      const departmentMatch = person.department?.toLowerCase().includes(searchLower) || false;

      return nameMatch || positionMatch || departmentMatch;
    });
  }
}
